import { useContext, useEffect } from "react";
import FormStep from "../../../services/models/formModels/MultiStepForm/formStep.model";
import OnlineOrderValuesModel from "../../../services/models/formModels/onlineOrderValues.model";
import CustomText from "../../atomic/CustomText";
import Button from "../../organism/Button";
import DetailRenderer from "./DetailRenderer";
import { PopUpContext } from "../../../services/context/popupContext";
import { useOktaAuth } from "@okta/okta-react";
import CreatePatientPopup from "../../popups/createPatientPopup";
import OnlineOrderPopupValues from "../../../services/models/formModels/onlineOrderPopupValues.model";
import { ApplicationState } from "../../../services/context/ApplicationStateContext";
import useOrganizations from "../../../services/utils/custom hooks/useOrganizations";
import usePhysiciansOfOrg from "../../../services/utils/custom hooks/usePhysiciansOfOrg";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import usePatientDetail from "../../../services/utils/custom hooks/Patients/usePatientDetail";

function PatientInformationStep({
  values,
  setFieldValue,
  setFieldTouched,
  errors,
}: Readonly<FormStep>) {
  const orderValues: OnlineOrderValuesModel = values;
  const queryClient = useQueryClient();
  const history: any = useHistory();

  const {
    state: { selectedOrganization, selectedPhysician },
  } = useContext(ApplicationState);
  const oktaAuth = useOktaAuth();
  const { data: organizationsData } = useOrganizations();
  const { data: physiciansData } = usePhysiciansOfOrg();
  const profileData = queryClient.getQueryData<any>(["profile"]);
  const roles: string[] = profileData?.user?.roles ?? [];
  const isPhysicianOnly = roles.length === 1 && roles.includes("Physician");
  const { data, isLoading } = usePatientDetail(orderValues.patientId);

  const org = organizationsData?.data.find(
    (org) => org.id === selectedOrganization
  );
  const clinician = isPhysicianOnly
    ? profileData.user
    : physiciansData?.data.find(
        (clinician) => clinician.id === selectedPhysician
      );

  const clinicianName = `${!!clinician?.firstName ? clinician.firstName : ""} ${
    !!clinician?.lastName ? clinician.lastName : ""
  }`;
  const { visible, setVisible, setPopUp, setPopUpTitle } =
    useContext(PopUpContext);

  function updateOnlineOrderValues(data: OnlineOrderPopupValues) {
    Object.keys(data).forEach((key: string) => {
      type ValueKey = keyof typeof data;
      const myKey = key as ValueKey;
      if (key === "securityInfo") {
        Object.keys(data.securityInfo).forEach((key: string) => {
          type ValueKey = keyof typeof data.securityInfo;
          const myKey = key as ValueKey;
          setFieldValue(
            `patientInfo.securityInfo.${key}`,
            data?.securityInfo?.[myKey]
          );
          setTimeout(() => {
            setFieldTouched(`patientInfo.securityInfo.${key}`, true);
          }, 500);
        });
      } else {
        setFieldValue(`patientInfo.${key}`, data?.[myKey]);
        setFieldTouched(`patientInfo.${key}`, true);
      }
    });
  }

  function editPatient() {
    setVisible(!visible);
    setPopUpTitle("Create a Patient");
    setPopUp(
      <CreatePatientPopup
        oktaAuth={oktaAuth}
        // initialStep={data?.data.ssn === "" || data?.data.mrn === "" ? 3 : 1}
        initialStep={1}
        patientInformation={{
          ...orderValues.patientInfo,
          testTypeId: values.testTypeId,
        }}
        values={values}
        setOnlineOrderValues={updateOnlineOrderValues}
      />
    );
  }
  const disableEditButton = isLoading
    ? true
    : data?.data?.isEditable === false &&
      !(data?.data?.ssn === "" || data?.data?.mrn === "")
    ? true
    : history?.location?.state?.tourPopup
    ? true
    : false;

  return (
    <div className="lg:flex">
      <div
        className={`p-6 border rounded-md mb-3 lg:mb-0 shadow-md lg:mr-[15px] lg:w-[345px] ${
          !!errors?.patientInfo ? "border-requiredRed" : ""
        }`}
      >
        <div className="flex justify-between items-center">
          <CustomText
            text={"Patient Information"}
            fontWeight="font-medium"
            fontSize="text-base"
            className="!text-royalBlue"
          />
          <Button
            textInformation={{ text: "Edit", fontWeight: "font-semibold" }}
            buttonTheme="secondary"
            onClick={editPatient}
            disabled={disableEditButton}
          />
        </div>
        <DetailRenderer
          containerClass=""
          data={{
            "First Name": orderValues.patientInfo.firstName,
            "Last Name": orderValues.patientInfo.lastName,
            "Date of Birth": orderValues.patientInfo.birthDate,
            "Sex at Birth": orderValues.patientInfo.gender,
          }}
          groupFields={[
            "First Name",
            "Last Name",
            "Date of Birth",
            "Sex at Birth",
          ]}
          obsecureFields={[
            "First Name",
            "Last Name",
            "Date of Birth",
            "Sex at Birth",
          ]}
          hiddenFields={[]}
        />
      </div>
      <div className="p-6 border rounded-md shadow-md lg:w-[345px]">
        <div className="flex justify-between ">
          <CustomText
            text={"Physician Association"}
            fontWeight="font-medium"
            fontSize="text-base"
            className="!text-royalBlue mb-3"
          />
        </div>
        <DetailRenderer
          data={{
            Organization: org?.name ?? "---",
            Physician: clinicianName === " " ? "---" : clinicianName,
          }}
          groupFields={[]}
          hiddenFields={[]}
        />
      </div>
    </div>
  );
}

export default PatientInformationStep;
